/* Hallmark · macrostructure: Single Column Report (app) · tone: technical · theme: console-system */
import {
  Button,
  buttonVariants,
  CodeViewer,
  Skeleton,
} from '@xhubio-saas/core-frontend/ui-components'
import {
  type CatalogCountry,
  type ConversionMode,
  type ConversionResult,
  extractAllowedCountries,
  type FormatDetectionOutcome,
  type FormatOption,
  findCatalogCountry,
  getFormatOptions,
  getSampleInvoice,
  getSampleTemplate,
  type NormalizedConversionResult,
  type RestApiCallState,
  type SavedTemplate,
  type SitzungsschluesselZustand,
  type TemplateSelection,
  type UploadedFile,
} from '@xhubio-saas/invoice-frontend/plugin-invoice-api'
import { RefreshCw } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import {
  type DetectionState,
  normalizeConversionResult,
  toDetectionState,
} from './conversion/bridge'
import {
  CONVERSION_MODES,
  ConversionOrder,
  MISSING_HINT_ID,
  type MissingRequirement,
} from './conversion/ConversionOrder'
import { ConversionResultSection } from './conversion/ConversionResultSection'
import { DocumentDrop, type DroppedDocument } from './conversion/DocumentDrop'
import { Workbench, type WorkbenchPanel } from './validation/Workbench'

/**
 * **Konvertierung** — Rechnungsdaten (JSON) zu PDF oder E-Rechnung, oder eine E-Rechnung zurueck zu JSON.
 *
 * 🔴 Single Column Report (Hallmark-Redesign 2026-10-10, Plan `und-jetzt-analysiere-was-pure-turtle`): EINE Spalte
 * bei jeder Breite, nach dem Muster der Validierung — Auftrag → Eingabe-Werkbank → (nach dem Lauf) Ergebnis-Kopfzeile
 * → Fehler/Hinweise als Report → Ergebnis-Werkbank. Vorher zweispaltig mit leerem „PDF Vorschau"-Kasten rechts.
 *
 * ⚪ App-lokal statt im Paket: die Paket-Seite (`InvoiceConversionPage`/`ConversionCard`, invoice-frontend 3.52.0)
 * bleibt unveraendert und wird hier nur nicht mehr eingebunden. Verhalten wie dort: konvertiert wird nur ueber die
 * REST-Tuer mit Sitzungsschluessel (KW-003), Erkennung ueber die eingespritzte `onDetectFormat` (tRPC), Kontingent-
 * Abweisung mit Weg zu `/subscription`, Laender nach Entitlement je Richtung, Richtung im URL-Hash (`#toPdf`).
 * Was das Paket nicht exportiert, steht in `conversion/bridge.ts` (Issue an invoice-frontend, Lane ib-660).
 */

const STORAGE_INPUT_TAB = 'invoice-conversion:input-tab'

/** Wartezeit, bevor ein eingefuegter Beleg zur Erkennung geht (wie die Paket-Karte). */
const DETECTION_DELAY_MS = 300

/** Richtung aus dem URL-Hash (`#toEInvoice`) — so wie die Paket-Seite sie ueber Neuladen hinweg haelt. */
function modeFromUrl(fallback: ConversionMode): ConversionMode {
  if (typeof window === 'undefined') return fallback
  const hash = window.location.hash.slice(1)
  return (CONVERSION_MODES as readonly string[]).includes(hash)
    ? (hash as ConversionMode)
    : fallback
}

function writeModeToUrl(mode: ConversionMode): void {
  if (typeof window === 'undefined') return
  window.history.replaceState(
    null,
    '',
    `${window.location.pathname}${window.location.search}#${mode}`
  )
}

/** Der Fehlercode im Rumpf einer Antwort (`{ error: 'NO_EMBEDDED_XML', … }`) — `null`, wenn keiner da ist. */
function responseCode(body: string | undefined): string | null {
  if (!body) return null
  try {
    const parsed: unknown = JSON.parse(body)
    if (typeof parsed !== 'object' || parsed === null) return null
    const { error, code } = parsed as { error?: unknown; code?: unknown }
    if (typeof code === 'string') return code
    return typeof error === 'string' ? error : null
  } catch {
    return null
  }
}

/** ZUGFeRD/Factur-X: PDF mit eingebettetem XML — traegt eine Vorlage und wird als PDF gelesen. */
const isPdfEmbedded = (format: FormatOption | null): boolean => format?.requiresPdfTemplate ?? false

export interface ConvertParams {
  mode: ConversionMode
  file: UploadedFile
  format: FormatOption | null
  templateFile?: UploadedFile
  templateId?: string
  countryCode: string
}

export interface InvoiceConversionPageProps {
  catalog: CatalogCountry[]
  catalogLoading?: boolean
  catalogError?: string | null
  /** Entitlements des Tarifs (`null` = keine Grenze) — gefiltert wird je Richtung. */
  billingEntitlements?: string[] | null
  /** Der Aufruf (`useInvoiceConversion().convert` ueber den REST-Client). */
  onConvert: (params: ConvertParams) => Promise<ConversionResult>
  /**
   * Quellformat-Erkennung — fehlt sie, gibt es „Automatisch erkennen" nicht. Ein PDF kommt als Base64 mit
   * `mimeType` `application/pdf`, ein XML als Text ohne `mimeType` (Plan API 10-10/09, E10).
   */
  onDetectFormat?: (
    content: string,
    countryCode: string,
    mimeType?: 'application/pdf'
  ) => Promise<FormatDetectionOutcome>
  requestState?: RestApiCallState | null
  apiKey?: string | null
  maskedApiKey?: string | null
  onResetRequestState?: () => void
  savedTemplates?: SavedTemplate[]
  /** Stand des Sitzungsschluessels — ohne `'da'` ist Konvertieren gesperrt und die Auftragskarte nennt den Grund. */
  sitzungsschluessel?: SitzungsschluesselZustand
  initialMode?: ConversionMode
}

function virtualFile(content: string, name: string, type: string): UploadedFile {
  return { file: new File([content], name, { type }), content, name, type }
}

export function InvoiceConversionPage({
  catalog,
  catalogLoading = false,
  catalogError = null,
  billingEntitlements = null,
  onConvert,
  onDetectFormat,
  requestState,
  apiKey,
  maskedApiKey,
  onResetRequestState,
  savedTemplates,
  sitzungsschluessel = 'da',
  initialMode = 'toPdf',
}: InvoiceConversionPageProps) {
  const { t } = useTranslation()
  // 🔴 Ein zweites `t` im Namensraum `invoiceConversion`: die Saetze der Paket-Bausteine (Laender, Formate,
  // Kontingent, Inspector) liegen dort. Meine eigenen Saetze stehen in `common` unter `invoiceConversionPage`.
  const { t: tPackageRaw } = useTranslation('invoiceConversion')
  const tPackage = tPackageRaw as unknown as (
    key: string,
    params?: Record<string, string>
  ) => string

  const [mode, setModeState] = useState<ConversionMode>(() => modeFromUrl(initialMode))
  const [country, setCountry] = useState<string | null>(null)
  const [format, setFormat] = useState<FormatOption | null>(null)
  const [autoDetect, setAutoDetect] = useState(false)
  const [detection, setDetection] = useState<DetectionState | null>(null)
  const detectionRun = useRef(0)

  const [input, setInput] = useState('')
  const [inputType, setInputType] = useState<
    'application/json' | 'text/xml' | 'application/pdf' | null
  >(null)
  const [droppedDoc, setDroppedDoc] = useState<DroppedDocument | null>(null)

  const [template, setTemplate] = useState<TemplateSelection>({ type: 'none' })
  const [templateContent, setTemplateContent] = useState('')
  const [templateFileName, setTemplateFileName] = useState<string | null>(null)

  const [running, setRunning] = useState(false)
  const [sampleLoading, setSampleLoading] = useState(false)
  const [inputError, setInputError] = useState<string | null>(null)
  const [result, setResult] = useState<NormalizedConversionResult | null>(null)
  /** Richtung und Format des Laufs, aus dem `result` stammt — die Kopfzeile nennt, was erzeugt WURDE. */
  const [resultOf, setResultOf] = useState<{
    mode: ConversionMode
    format: FormatOption | null
  } | null>(null)

  const allowedCountries = useMemo(
    () => extractAllowedCountries(billingEntitlements, mode),
    [billingEntitlements, mode]
  )
  const catalogCountry = useMemo(
    () => (country ? (findCatalogCountry(catalog, country) ?? null) : null),
    [catalog, country]
  )

  /** Jede Aenderung an der Eingabe verwirft das Ergebnis — es gehoert zu der Eingabe, mit der es entstand. */
  const clearResult = useCallback(() => {
    setResult(null)
    setResultOf(null)
    setInputError(null)
  }, [])

  // Nur ein erlaubtes Land: gleich gewaehlt (wie die Paket-Karte).
  useEffect(() => {
    if (allowedCountries && allowedCountries.length === 1 && !country) {
      const only = allowedCountries[0]
      if (only) setCountry(only)
    }
  }, [allowedCountries, country])

  const stopDetection = useCallback(() => {
    detectionRun.current++
    setAutoDetect(false)
    setDetection(null)
  }, [])

  const changeMode = useCallback(
    (next: ConversionMode) => {
      // Die Eingabe ueberlebt den Wechsel, wenn die Sprache bleibt (JSON ↔ JSON); `toJson` erwartet XML/PDF.
      const sameLanguage = (mode === 'toJson') === (next === 'toJson')
      setModeState(next)
      writeModeToUrl(next)
      onResetRequestState?.()
      if (!sameLanguage) {
        setInput('')
        setInputType(null)
        setDroppedDoc(null)
      } else {
        setInputType(next === 'toJson' ? 'text/xml' : 'application/json')
      }
      setTemplate({ type: 'none' })
      setTemplateContent('')
      setTemplateFileName(null)
      setFormat(null)
      stopDetection()
      clearResult()
    },
    [mode, onResetRequestState, stopDetection, clearResult]
  )

  const changeCountry = useCallback(
    (next: string) => {
      // Das Land ist die Wurzel: Format, Vorlage, Eingabe, Ergebnis und Inspector gehoeren zum alten.
      stopDetection()
      setCountry(next)
      setFormat(null)
      setInput('')
      setInputType(null)
      setDroppedDoc(null)
      setTemplate({ type: 'none' })
      setTemplateContent('')
      setTemplateFileName(null)
      clearResult()
      onResetRequestState?.()
    },
    [stopDetection, clearResult, onResetRequestState]
  )

  const changeFormat = useCallback(
    (next: FormatOption) => {
      // 🔴 Die Wahl von Hand beendet jede Erkennung, auch eine laufende.
      stopDetection()
      setFormat(next)
      clearResult()
    },
    [stopDetection, clearResult]
  )

  const chooseAutoDetect = useCallback(() => {
    setAutoDetect(true)
    setFormat(null)
    clearResult()
  }, [clearResult])

  const changeTemplate = useCallback(
    (selection: TemplateSelection, fileName?: string) => {
      setTemplate(selection)
      setTemplateFileName(selection.type === 'upload' ? (fileName ?? null) : null)
      setTemplateContent(
        selection.type === 'default'
          ? JSON.stringify(getSampleTemplate(country), null, 2)
          : selection.type === 'upload'
            ? selection.content
            : ''
      )
      clearResult()
    },
    [country, clearResult]
  )

  const changeInput = useCallback(
    (content: string) => {
      setInput(content)
      setInputType(mode === 'toJson' ? 'text/xml' : 'application/json')
      clearResult()
    },
    [mode, clearResult]
  )

  const changeDocument = useCallback(
    (doc: DroppedDocument | null) => {
      setDroppedDoc(doc)
      setInput(doc?.content ?? '')
      setInputType(doc?.contentType ?? null)
      clearResult()
    },
    [clearResult]
  )

  // Erkennung: laeuft, wenn „Automatisch erkennen" gewaehlt ist und ein Beleg da ist. Ein Zaehler verwirft spaete
  // Antworten — eine Wahl von Hand oder eine neuere Erkennung hat immer Vorrang.
  useEffect(() => {
    if (!onDetectFormat || !autoDetect || mode !== 'toJson') return
    if (!input || !country) {
      setDetection(null)
      return
    }
    const run = ++detectionRun.current
    setDetection({ status: 'detecting' })
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const outcome =
            inputType === 'application/pdf'
              ? await onDetectFormat(input, country, 'application/pdf')
              : await onDetectFormat(input, country)
          if (detectionRun.current !== run) return
          const options = catalogCountry ? getFormatOptions(catalogCountry, 'toJson') : []
          const state = toDetectionState(outcome, options)
          setDetection(state)
          setFormat(state.status === 'resolved' ? state.option : null)
        } catch {
          if (detectionRun.current !== run) return
          setDetection({ status: 'failed' })
        }
      })()
    }, DETECTION_DELAY_MS)
    return () => clearTimeout(timer)
  }, [onDetectFormat, autoDetect, mode, input, inputType, country, catalogCountry])

  const insertSample = useCallback(async () => {
    const land = country ?? 'DE'
    const adoptDe = country === null && (!allowedCountries || allowedCountries.includes('DE'))
    clearResult()
    if (mode !== 'toJson') {
      setInput(JSON.stringify(getSampleInvoice(country), null, 2))
      setInputType('application/json')
      if (adoptDe) setCountry('DE')
      return
    }
    // E-Rechnung → JSON: das Beispiel wird ERZEUGT (ein Lauf JSON → E-Rechnung), nicht mitgeliefert.
    // 🔴 Mit einem Zielformat: die Paket-Karte rief hier `format: null`, der Hook antwortete „Format is required" —
    // der Knopf meldete immer „Beispiel konnte nicht erzeugt werden". Gewaehlt wird das erste Format des Landes, das
    // sich auch lesen laesst, ohne PDF-Huelle (XML ins Feld).
    const katalogLand = findCatalogCountry(catalog, land)
    const readable = katalogLand ? getFormatOptions(katalogLand, 'toJson') : []
    const target = (katalogLand ? getFormatOptions(katalogLand, 'toEInvoice') : []).find(
      (o) => !o.requiresPdfTemplate && readable.some((r) => r.apiFormat === o.apiFormat)
    )
    if (!target) {
      setInputError(tPackage('actions.sampleFailed'))
      return
    }
    const json = JSON.stringify(getSampleInvoice(land), null, 2)
    setSampleLoading(true)
    try {
      const generated = normalizeConversionResult(
        await onConvert({
          mode: 'toEInvoice',
          file: virtualFile(json, 'invoice.json', 'application/json'),
          format: target,
          countryCode: land,
        })
      )
      if (!generated.success || !generated.data) {
        setInputError(tPackage('actions.sampleFailed'))
        return
      }
      setInput(generated.data)
      setInputType('text/xml')
      setDroppedDoc(null)
      if (adoptDe) setCountry('DE')
      // Das Quellformat ist bekannt — dasselbe, in dem das Beispiel eben entstand.
      stopDetection()
      setFormat(readable.find((r) => r.apiFormat === target.apiFormat) ?? null)
    } finally {
      setSampleLoading(false)
    }
  }, [country, allowedCountries, mode, catalog, onConvert, tPackage, clearResult, stopDetection])

  const needsTemplate = mode === 'toPdf' || (mode === 'toEInvoice' && isPdfEmbedded(format))
  const hasTemplate =
    templateContent !== '' || template.type === 'saved' || template.type === 'none'

  /** Die erste fehlende Angabe — in der Reihenfolge, in der die Seite sie abfragt. Ohne Schluessel hilft nichts. */
  const missing: MissingRequirement =
    sitzungsschluessel !== 'da'
      ? 'session'
      : country === null
        ? 'country'
        : mode !== 'toPdf' && format === null
          ? 'format'
          : input === ''
            ? 'input'
            : null
  // Eine Vorlage fehlt nur, wenn Beispiel- oder eigene Vorlage gewaehlt und ihr Inhalt geleert wurde.
  const missingTemplate = missing === null && needsTemplate && !hasTemplate
  const canConvert = missing === null && !missingTemplate && !running

  const convert = useCallback(async () => {
    if (!country || !input) return
    setRunning(true)
    setInputError(null)
    try {
      const type = inputType ?? 'application/json'
      const name =
        type === 'application/pdf' ? 'input.pdf' : type === 'text/xml' ? 'input.xml' : 'input.json'
      const raw = await onConvert({
        mode,
        file: virtualFile(input, name, type),
        format,
        templateFile: templateContent
          ? virtualFile(templateContent, 'template.json', 'application/json')
          : undefined,
        templateId: template.type === 'saved' ? template.id : undefined,
        countryCode: country,
      })
      setResult(normalizeConversionResult(raw))
      setResultOf({ mode, format })
    } catch (err) {
      setResult({
        success: false,
        error: err instanceof Error ? err.message : tPackage('error.conversionFailed'),
      })
      setResultOf({ mode, format })
    } finally {
      setRunning(false)
    }
  }, [country, input, inputType, onConvert, mode, format, templateContent, template, tPackage])

  const reset = useCallback(() => {
    stopDetection()
    setInput('')
    setInputType(null)
    setDroppedDoc(null)
    setTemplate({ type: 'none' })
    setTemplateContent('')
    setTemplateFileName(null)
    setFormat(null)
    setCountry(null)
    clearResult()
  }, [stopDetection, clearResult])

  if (catalogLoading && catalog.length === 0) {
    return (
      <div className="container mx-auto space-y-4 px-4 py-6" data-testid="invoice-loading">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    )
  }
  if (catalogError && catalog.length === 0) {
    return (
      <div className="container mx-auto px-4 py-6">
        <p
          className="rounded-lg border border-destructive/40 p-4 text-sm text-destructive"
          role="alert"
        >
          {catalogError}
        </p>
      </div>
    )
  }

  // ─── Eingabe-Werkbank ──────────────────────────────────────────────────────────────────────────
  const sampleButton =
    // Im Lesemodus geht das Beispiel durch dieselbe Tuer — ohne Schluessel fehlt der Knopf (die Karte nennt den Grund).
    mode === 'toJson' && sitzungsschluessel !== 'da'
      ? undefined
      : {
          label: sampleLoading
            ? tPackage('actions.sampleLoading')
            : tPackage('actions.insertSample'),
          onInsert: () => void insertSample(),
        }
  const dropzoneLabels = {
    dragDropText: tPackage('codeViewer.dragDropText'),
    selectFileText: tPackage('codeViewer.selectFileText'),
  }
  // Schnellsuche der Eingabe-Editoren (core-frontend ≥ 5.73.0) — dieselben Sätze wie in der Ergebnis-Ansicht
  const searchLabels = {
    placeholder: t('invoiceValidation.content.search.placeholder', 'Suchen…'),
    inputLabel: t('invoiceValidation.content.search.inputLabel', 'Im Code suchen'),
    next: t('invoiceValidation.content.search.next', 'Nächster Treffer'),
    previous: t('invoiceValidation.content.search.previous', 'Vorheriger Treffer'),
    clear: t('invoiceValidation.content.search.clear', 'Suche zurücksetzen'),
    noMatches: t('invoiceValidation.content.search.noMatches', '0 Treffer'),
  }
  const inputPanels: WorkbenchPanel[] = []
  // Im Auto-Modus ohne Format nimmt die Ablage PDF und XML an (wie die Paket-Karte, Plan API 10-10/09 E16 A): der
  // XML-Editor liest eine Datei als Text, und die PDF-Bytes waeren verloren. Ergibt die Erkennung ein XML-Format,
  // ist der Text wieder im Editor.
  const documentDrop = isPdfEmbedded(format) || (autoDetect && format === null)
  if (mode === 'toJson') {
    inputPanels.push({
      id: 'document',
      label: t('invoiceConversionPage.input.tab.document'),
      hint: documentDrop ? 'PDF · XML' : 'XML',
      content: documentDrop ? (
        <DocumentDrop value={droppedDoc} onChange={changeDocument} />
      ) : (
        <CodeViewer
          value={input}
          onChange={changeInput}
          readonly={false}
          language="xml"
          label="XML"
          height={400}
          expandable={input !== ''}
          expandTitle={t('invoiceConversionPage.input.tab.document')}
          foldable
          dropzoneLabels={dropzoneLabels}
          searchLabels={searchLabels}
          sampleButton={sampleButton}
        />
      ),
    })
  } else {
    inputPanels.push({
      id: 'data',
      label: t('invoiceConversionPage.input.tab.data'),
      hint: 'JSON',
      content: (
        <CodeViewer
          value={input}
          onChange={changeInput}
          readonly={false}
          language="json"
          label="JSON"
          height={400}
          expandable={input !== ''}
          expandTitle={t('invoiceConversionPage.input.tab.data')}
          foldable
          dropzoneLabels={dropzoneLabels}
          searchLabels={searchLabels}
          sampleButton={sampleButton}
        />
      ),
    })
    if (needsTemplate && (template.type === 'default' || template.type === 'upload')) {
      inputPanels.push({
        id: 'template',
        label: t('invoiceConversionPage.input.tab.template'),
        hint: 'JSON',
        content: (
          <CodeViewer
            value={templateContent}
            onChange={(v) => {
              setTemplateContent(v)
              clearResult()
            }}
            readonly={false}
            language="json"
            label="JSON"
            height={400}
            expandable={templateContent !== ''}
            expandTitle={t('invoiceConversionPage.input.tab.template')}
            foldable
            dropzoneLabels={dropzoneLabels}
            searchLabels={searchLabels}
          />
        ),
      })
    }
  }

  const describedBy =
    missing === 'input'
      ? 'invoice-conversion-missing-input'
      : missing
        ? MISSING_HINT_ID[missing]
        : missingTemplate
          ? 'invoice-conversion-missing-template'
          : undefined
  // Die Antwort traegt den Code, die Meldung des Pakets nicht mehr (`convertToJson` flacht ihn zur Meldung ab).
  const noEmbeddedXml =
    resultOf?.mode === 'toJson' &&
    result?.success === false &&
    responseCode(requestState?.lastResponse?.body) === 'NO_EMBEDDED_XML'
  const resultFormatLabel =
    resultOf?.mode === 'toPdf'
      ? 'PDF'
      : resultOf?.mode === 'toJson'
        ? 'JSON'
        : (resultOf?.format?.label ?? '')

  return (
    <div className="container mx-auto space-y-6 px-4 py-6" data-testid="invoice-page">
      <div>
        <h1 className="min-w-0 text-3xl tracking-tight [overflow-wrap:anywhere]">
          {tPackage('page.title')}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('invoiceConversionPage.subtitle')}</p>
      </div>

      {/* 🔴 Eine Spalte bei jeder Breite, volle Seitenbreite, nichts klebt (Muster Validierung). `min-w-0`: ein Kind
          waechst sonst mit seinem breitesten Inhalt (CodeViewer, lange Kennungen). */}
      <div className="grid grid-cols-1 gap-6" data-testid="invoice-conversion-layout">
        <ConversionOrder
          catalog={catalog}
          allowedCountries={allowedCountries}
          country={country}
          onCountryChange={changeCountry}
          mode={mode}
          onModeChange={changeMode}
          format={format}
          onFormatChange={changeFormat}
          autoDetectOffered={mode === 'toJson' && onDetectFormat !== undefined}
          isAutoDetect={autoDetect}
          onAutoDetect={chooseAutoDetect}
          detection={detection}
          needsTemplate={needsTemplate}
          template={template}
          templateFileName={templateFileName}
          onTemplateChange={changeTemplate}
          savedTemplates={savedTemplates}
          sitzungsschluessel={sitzungsschluessel}
          quota={result?.quota}
          missing={missing}
          tPackage={tPackage}
        />

        <div className="min-w-0 space-y-3" data-testid="invoice-conversion-input">
          {/* Eine Werkbank mit EINEM Tab waere eine Leiste ohne Wahl — dann steht der Editor allein (seine Kopfzeile
              nennt die Sprache). Tabs erst, wenn es eine zweite Eingabe gibt (eigene Vorlage). */}
          {inputPanels.length > 1 ? (
            <Workbench
              panels={inputPanels}
              storageKey={STORAGE_INPUT_TAB}
              testIdPrefix="invoice-conversion-input"
              label={t('invoiceConversionPage.input.title')}
            />
          ) : (
            <section
              aria-label={t('invoiceConversionPage.input.title')}
              data-testid="invoice-conversion-input-single"
            >
              {inputPanels[0]?.content}
            </section>
          )}
          <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-2">
            {inputError && (
              <p
                className="mr-auto text-sm text-destructive"
                role="alert"
                data-testid="invoice-input-error"
              >
                {inputError}
              </p>
            )}
            {missing === 'input' && !inputError && (
              <p
                id="invoice-conversion-missing-input"
                className="mr-auto text-sm text-muted-foreground"
              >
                {tPackage('actions.missingInput')}
              </p>
            )}
            {missingTemplate && !inputError && (
              <p
                id="invoice-conversion-missing-template"
                className="mr-auto text-sm text-muted-foreground"
              >
                {tPackage('error.noTemplateSelected')}
              </p>
            )}
            {(input !== '' || result) && (
              <Button
                variant="outline"
                className="whitespace-nowrap"
                onClick={reset}
                data-testid="invoice-btn-reset"
              >
                {tPackage('actions.reset')}
              </Button>
            )}
            <Button
              className="whitespace-nowrap"
              onClick={() => void convert()}
              disabled={!canConvert}
              aria-describedby={!canConvert ? describedBy : undefined}
              data-testid="invoice-btn-convert"
            >
              {running ? (
                <>
                  <RefreshCw
                    className="h-4 w-4 animate-spin motion-reduce:animate-none"
                    aria-hidden="true"
                  />
                  {tPackage('actions.converting')}
                </>
              ) : (
                tPackage('actions.convert')
              )}
            </Button>
          </div>
        </div>

        {result && resultOf && noEmbeddedXml ? (
          // E12 (Plan API 2026-11-01/01): ein normales PDF ohne E-Rechnung ist kein Fehler des Nutzers, sondern der
          // falsche Weg — statt des rohen `NO_EMBEDDED_XML` der Weg zur Seite, die genau das kann.
          <section
            className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-lg border bg-card p-4"
            data-testid="invoice-conversion-no-embedded-xml"
          >
            <p className="min-w-0 text-sm">{t('invoiceConversionPage.noEmbeddedXml.body')}</p>
            <Link
              to="/invoice-from-pdf"
              className={buttonVariants({ variant: 'default', className: 'ml-auto shrink-0' })}
            >
              {t('invoiceConversionPage.noEmbeddedXml.cta')}
            </Link>
          </section>
        ) : result && resultOf ? (
          <ConversionResultSection
            result={result}
            mode={resultOf.mode}
            formatLabel={resultFormatLabel}
            requestState={apiKey ? requestState : null}
            apiKey={apiKey}
            maskedApiKey={maskedApiKey}
            tPackage={tPackage}
          />
        ) : null}
      </div>
    </div>
  )
}
